import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import ProductPlaceholder from "@/components/ProductPlaceholder";
import BuyOrNotify from "@/components/BuyOrNotify";
import Seo from "@/components/Seo";
import { COLLECTIONS } from "@/data/content";

const CollectionTile = ({ collection }) => {
  const [open, setOpen] = useState(false);
  return (
    <article
      data-testid={`collection-tile-${collection.id}`}
      className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col transition-transform duration-300 hover:-translate-y-1"
    >
      <Link
        to={`/collections/${collection.id}`}
        data-testid={`collection-card-toggle-${collection.id}`}
        className="block focus:outline-none focus:ring-2 focus:ring-sage"
      >
        <ProductPlaceholder
          label={`UNSTUCK ${collection.name} deck`}
          ratio="aspect-[4/3]"
          testId={`collection-image-${collection.id}`}
          src={collection.image}
          alt={`UNSTUCK ${collection.name} deck and activity cards`}
        />
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-2">
          <Link
            to={`/collections/${collection.id}`}
            data-testid={`collection-name-toggle-${collection.id}`}
            className="focus:outline-none focus:ring-2 focus:ring-sage rounded"
          >
            <h2 className="font-heading font-bold text-2xl text-ink hover:text-sage transition-colors">{collection.name}</h2>
          </Link>
          <span
            data-testid={`collection-price-${collection.id}`}
            className="shrink-0 text-sm font-semibold uppercase tracking-wider text-gold-dark bg-gold-light rounded-full px-3 py-1"
          >
            {collection.price ? `$${collection.price.toFixed(2)}` : "Price TBD"}
          </span>
        </div>
        <p className="text-base leading-relaxed text-ink/75 mb-4">{collection.short}</p>
        {open && (
          <p className="text-base leading-relaxed text-ink/80 mb-4 bg-cream rounded-xl p-4" data-testid={`collection-more-${collection.id}`}>
            {collection.long}
          </p>
        )}
        <div className="mt-auto space-y-3">
          <BuyOrNotify
            lookupKey={collection.lookupKey}
            price={collection.price}
            label={collection.price != null ? `Buy This Deck — $${collection.price.toFixed(2)}` : "Buy This Deck"}
            testId={`collection-buy-${collection.id}`}
            className="w-full bg-sage text-white hover:bg-sage-dark"
          />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid={`collection-learn-more-${collection.id}`}
            className="inline-flex items-center gap-2 text-sage font-semibold hover:text-sage-dark transition-colors py-2 focus:outline-none focus:ring-2 focus:ring-sage rounded"
          >
            {open ? "Show less" : "Learn More"}
            <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
};

const Collections = () => {
  return (
    <div data-testid="collections-page" className="bg-cream">
      <Seo
        title="Explore UNSTUCK Collections — 15-Minute Activity Decks for Every Age"
        description="Nine UNSTUCK collections: Seniors, Original Reset, Kids, Teen Boys, Teen Girls, Content Creator, Dad, Family, and Couples. Simple 15-minute activity cards that help anyone take one small step."
        path="/collections"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="max-w-3xl mb-14">
          <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">
            The UNSTUCK family
          </p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-ink mb-6">
            Explore UNSTUCK Collections
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-4">
            Every UNSTUCK deck is built on the same idea: one simple 15-minute activity that helps someone
            take a small step and build momentum. UNSTUCK Seniors is our featured collection — these eight
            decks bring the same approach to the rest of life.
          </p>
          <p className="text-base leading-relaxed text-ink/70">
            Each deck is $34.99 plus shipping and its Buy button goes straight to secure checkout.{" "}
            <Link to="/contact" data-testid="collections-contact-link" className="text-sage font-semibold hover:text-sage-dark underline underline-offset-4">
              Contact us
            </Link>{" "}
            with any questions about a specific deck.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16" data-testid="collections-grid">
          {COLLECTIONS.map((collection) => (
            <CollectionTile key={collection.id} collection={collection} />
          ))}
        </div>

        <div className="bg-sage rounded-2xl p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-3">
              Start with UNSTUCK Seniors
            </h2>
            <p className="text-lg text-cream/90">
              Our featured collection, available today as a complete program or a single deck.
            </p>
          </div>
          <Link
            to="/seniors"
            data-testid="collections-seniors-cta"
            className="inline-flex shrink-0 items-center justify-center gap-2 bg-gold text-ink hover:bg-gold-dark hover:text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-sage"
          >
            Shop UNSTUCK Seniors <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Collections;
